import type { AllocationItem, AllocationLocation } from "~fulfill/orders/columnDefs/allocationModalColDef";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
	sumLocationAvailability,
	wholeEachesAvailable,
	withAvailabilityAfterAllocation
} from "~fulfill/orders/helpers/allocationStrategyFill";

export type AllocationStrategy = "highestGrade" | "balanced" | "fifo" | "lifo" | "priority" | "singleLocation";

interface AllocationState {
	allocations: Record<string, AllocationItem>;
	allocationsOrderId: string;
	priorityGrades: string[];
	priorityLocations: string[];
	selectedGrade: string;
	selectedLocation: string;
	strategy: AllocationStrategy;
}

const initialState: AllocationState = {
	allocations: {},
	allocationsOrderId: "",
	priorityGrades: [],
	priorityLocations: [],
	selectedGrade: "",
	selectedLocation: "",
	strategy: "highestGrade"
};

export const allocationSlice = createSlice({
	initialState,
	name: "allocation",
	reducers: {
		clearAllAllocations: (state) => {
			state.allocations = {};
			state.allocationsOrderId = "";
			state.selectedLocation = "";
			state.selectedGrade = "";
			state.strategy = "highestGrade";
			state.priorityLocations = [];
			state.priorityGrades = [];
		},
		initializeAllocationsForOrder: (
			state,
			action: PayloadAction<{ allocations: Record<string, AllocationItem>; orderId: string }>
		) => {
			state.allocations = action.payload.allocations;
			state.allocationsOrderId = action.payload.orderId;
		},
		setAllocations: (
			state,
			action: PayloadAction<{ allocations: Record<string, AllocationItem>; orderId: string }>
		) => {
			state.allocations = action.payload.allocations;
			state.allocationsOrderId = action.payload.orderId;
		},
		setPriorityGrades: (state, action: PayloadAction<string[]>) => {
			state.priorityGrades = action.payload;
		},
		setPriorityLocations: (state, action: PayloadAction<string[]>) => {
			state.priorityLocations = action.payload;
		},
		setSelectedGrade: (state, action: PayloadAction<string>) => {
			state.selectedGrade = action.payload;
		},
		setSelectedLocation: (state, action: PayloadAction<string>) => {
			state.selectedLocation = action.payload;
		},
		setStrategy: (state, action: PayloadAction<AllocationStrategy>) => {
			state.strategy = action.payload;
		},
		togglePriorityGrade: (state, action: PayloadAction<string>) => {
			const idx = state.priorityGrades.indexOf(action.payload);
			if (idx >= 0) {
				state.priorityGrades = state.priorityGrades.filter((g) => g !== action.payload);
			} else {
				state.priorityGrades.push(action.payload);
			}
		},
		togglePriorityLocation: (state, action: PayloadAction<string>) => {
			const idx = state.priorityLocations.indexOf(action.payload);
			if (idx >= 0) {
				state.priorityLocations = state.priorityLocations.filter((id) => id !== action.payload);
			} else {
				state.priorityLocations.push(action.payload);
			}
		},
		updateAllocation: (state, action: PayloadAction<{ id: string; item: AllocationItem }>) => {
			state.allocations[action.payload.id] = action.payload.item;
		},
		updateLocationAllocation: (
			state,
			action: PayloadAction<{
				allowOverallocation: boolean;
				grade: string;
				itemId: string;
				locationId: string;
				value: number;
			}>
		) => {
			const { allowOverallocation, grade, itemId, locationId, value } = action.payload;
			const item = state.allocations[itemId];
			if (!item) return;

			const newLocations = (item.locations || []).map((loc): AllocationLocation => {
				if (loc.id === locationId && loc.grade === grade) {
					const bounded = allowOverallocation ? value : Math.min(value, wholeEachesAvailable(loc.baseAvailable));
					const newAllocated = Math.max(0, bounded);
					return withAvailabilityAfterAllocation({ ...loc, allocated: newAllocated });
				}
				return { ...loc };
			});

			const newAllocated = newLocations.reduce((sum, loc) => sum + loc.allocated, 0);
			const newAvailable = sumLocationAvailability(newLocations);

			state.allocations[itemId] = {
				...item,
				allocated: newAllocated,
				available: newAvailable,
				locations: newLocations
			};
		}
	}
});

export const {
	clearAllAllocations,
	initializeAllocationsForOrder,
	setAllocations,
	setPriorityGrades,
	setPriorityLocations,
	setSelectedGrade,
	setSelectedLocation,
	setStrategy,
	togglePriorityGrade,
	togglePriorityLocation,
	updateAllocation,
	updateLocationAllocation
} = allocationSlice.actions;

export default allocationSlice.reducer;
